/**
 * The 19-byte OpusHead identification header (RFC 7845 §5.1).
 *
 * WebCodecs takes it as `AudioDecoderConfig.description`. Chrome will configure an
 * Opus decoder without one, but Firefox's implementation wants it, so we always
 * offer it first and only fall back to a bare config if that is rejected.
 */

/**
 * Encoder delay, in 48 kHz samples, that the decoder discards from the front of the
 * stream (RFC 7845 §4.2). This must match what the *sender's* encoder actually
 * prepended, because the decoder throws away exactly this much audio every time it
 * is configured — and `OpusAdapter.reset()` reconfigures on every discontinuity.
 *
 * 312 is libopus's lookahead at 48 kHz for the settings the plugin uses
 * (`opus_encoder_create(48000, ch, OPUS_APPLICATION_AUDIO)`, complexity 10, VBR).
 * Verified against libopus itself: the OpusHead libopus emits for that
 * configuration carries 312, at both 10 ms and 20 ms frame durations.
 *
 * The value here used to be 3840 — the figure some WebM muxers use as a codec
 * delay. That is 80 ms, i.e. four whole 20 ms frames, and declaring it made the
 * decoder swallow the first four frames after every configure and every reset.
 */
const LIBOPUS_PRE_SKIP_48K = 312;

export function buildOpusHead(
  channels: number,
  preSkip = LIBOPUS_PRE_SKIP_48K,
  inputSampleRate = 48_000,
  outputGain = 0,
): Uint8Array {
  const head = new Uint8Array(19);
  const view = new DataView(head.buffer);

  head.set([0x4f, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64], 0); // "OpusHead"
  head[8] = 1; // version
  head[9] = Math.max(1, Math.min(255, Math.trunc(channels)));
  view.setUint16(10, preSkip, true);
  view.setUint32(12, inputSampleRate, true);
  view.setInt16(16, outputGain, true);
  head[18] = 0; // channel mapping family 0: mono or plain stereo

  return head;
}
